import React, { ReactNode, useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Grid, Typography } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import {
  deleteGeoZone as deleteGeoZoneApi,
  getGeoZonesNoFormQuery,
  postGeoZone,
  putGeoZone,
} from '../../shared/api/api';
import style from './CompanyGeoZonesPanel.module.css';
import GeoZonesToolbarWidget from '../../widgets/geo-zones/GeoZonesToolbarWidget';
import GeoZonesMapWidget from '../../widgets/geo-zones/GeoZonesMapWidget';
import { coordsToString } from '../../shared/geo-map/lib';
import useDebounce from '../../shared/hooks/useDebounce';
import useShowError from '../../shared/lib/hooks/useShowError';
import GeoZonesSidebar from './components/GeoZonesSidebar';
import updateGeoZoneList from './utils/updateGeoZoneList';

interface GeoZone {
  geozoneId: number;
  id: number;
  name: string;
  area: string;
  polygon: number[][];
  customAugmentation?: {
    customAugId?: number;
    attrs?: Array<{
      group?: string;
      name?: string;
      value?: string;
    }>;
  };
  customAugmentationEx?: {
    view?: Record<string, string>;
  };
}

interface ErrorWithMessage {
  errorMessage?: string;
}

interface Company {
  address: string;
  companyId: number;
  name: string;
  site: string;
  tenantId: string;
}

interface Props {
  company: Company;
}

const debounceDelay = 500;
const PAGE_SIZE = 20;

// Отображает список геозон компании и карту с полигонами зон
function CompanyGeoZonesPanel({ company }: Props): ReactNode {
  // Список геозон с сервера
  const [geoZones, setGeoZones] = useState<GeoZone[]>([]);
  // Имя геозоны в текстовом поле
  const [nameGeoZone, setNameGeoZone] = useState<string>('');
  // Координаты создаваемой геозоны
  const [coordsGeoZone, setCoordsGeoZone] = useState(null);
  // Выбранные геозоны
  const [selectedGeoZones, setSelectedGeoZones] = useState<GeoZone[]>([]);
  // Редактируемая геозона
  const [changeableGeoZone, setChangeableGeoZone] = useState<GeoZone | null>(null);
  // Состояние создания полигона
  const [isCreatePolygon, setIsCreatePolygon] = useState(false);
  // Состояние создания геозоны
  const [isCreateGeoZone, setIsCreateGeoZone] = useState(false);
  // Кол-во изменений
  const [isChanged, setIsChanged] = useState(false);

  const [errors, setErrors] = useState({ name: false, polygon: false });

  const showError: (msg?: string) => void = useShowError();

  // Новые геозоны (всегда помещаются в начало списка)
  const [addedZones, setAddedZones] = useState<GeoZone[]>([]);
  // Все геозоны, состоящие из уникальных новых и подгруженных
  const [allGeoZones, setAllGeoZones] = useState<GeoZone[]>([]);

  // Номер последней подгруженной страницы
  const [page, setPage] = useState(0);

  const confirm = useConfirm();

  const [search, setSearch] = useState('');

  const [isExactSearch, setIsExactSearch] = useState(true);

  const [maxItems, setMaxItems] = useState(PAGE_SIZE);

  const [total, setTotal] = useState(0);

  const getGetGeoZonesFunc = (searchCurrent?: string, specificPage: number | undefined = undefined) => {
    const requestedPage = specificPage || specificPage === 0 ? specificPage : page;
    const query: {
      'companyId.equals': number;
      size: number;
      page: number;
      'name.contains'?: string;
      'name.equals'?: string;
      sort: string;
    } = {
      'companyId.equals': company?.companyId,
      page: requestedPage,
      size: PAGE_SIZE,
      sort: 'name,asc',
    };
    if (searchCurrent) {
      if (isExactSearch) {
        query['name.equals'] = searchCurrent;
      } else {
        query['name.contains'] = searchCurrent;
      }
    }
    getGeoZonesNoFormQuery(query)
      .then((res: { total: number; geozones: GeoZone[] }) => {
        setTotal(res.total);
        setGeoZones((prev) => {
          prev.splice(requestedPage * PAGE_SIZE, PAGE_SIZE, ...res.geozones);
          return [...prev];
        });
      })
      .catch((error: Error) => {
        throw error;
      });
  };

  // Удаление геозон
  const deleteGeoZones = () => {
    if (!selectedGeoZones.length) {
      return;
    }
    const idsToDelete = selectedGeoZones.map((z) => z.geozoneId);
    setGeoZones((prev) => prev.filter((z) => !idsToDelete.includes(z.geozoneId)));

    Promise.all(idsToDelete.map((id) => deleteGeoZoneApi(id)))
      .then(() => {
        idsToDelete.forEach((id) => {
          let pageToRefresh = Math.floor(geoZones.findIndex((z) => z.geozoneId === id) / PAGE_SIZE);
          while (pageToRefresh <= page) {
            getGetGeoZonesFunc(search, pageToRefresh);
            pageToRefresh += 1;
          }
        });

        setAddedZones((prev) => prev.filter((geoZone) => !idsToDelete.includes(geoZone.geozoneId)));
        setSelectedGeoZones([]);
        setNameGeoZone('');
      })
      .catch((error: ErrorWithMessage) => {
        showError(error.errorMessage);
      });
  };

  // Нажатие на геозону в списке
  const onSelectGeoZone = (geoZone: GeoZone) => {
    setSelectedGeoZones((prev) => {
      const isExist = prev.some((z) => z.geozoneId === geoZone.geozoneId);
      if (isExist) return prev.filter((z) => z.geozoneId !== geoZone.geozoneId);

      return [...prev, geoZone];
    });
    setIsCreatePolygon(false);
    setIsCreateGeoZone(false);
    setIsChanged(false);
    // Сбрасываем координаты, чтобы при переименовании другой геозоны не отправить чужой полигон (#13338)
    setCoordsGeoZone(null);
    setErrors({ name: false, polygon: false });
  };

  // Нажатие на кнопку создание геозоны
  const onClickCreate = () => {
    setChangeableGeoZone(null);
    setSelectedGeoZones([]);
    setCoordsGeoZone(null);
    setNameGeoZone('');
    setIsCreatePolygon((prev) => !prev);
    setIsCreateGeoZone(true);
  };

  useEffect(() => {
    const newAddedZones = [...addedZones].filter((z) => !geoZones.some((gz) => gz.geozoneId === z.geozoneId));
    if (newAddedZones.length !== addedZones.length) {
      setAddedZones(newAddedZones);
    }
    setAllGeoZones([...newAddedZones, ...geoZones]);
  }, [addedZones, geoZones]);

  useEffect(() => {
    if (selectedGeoZones.length === 1) {
      setChangeableGeoZone(selectedGeoZones[0]);
      setNameGeoZone(selectedGeoZones[0].name);
    } else {
      setChangeableGeoZone(null);
      setNameGeoZone('');
    }

    const rest = geoZones.filter((gz) => !selectedGeoZones.some((sz) => sz.geozoneId === gz.geozoneId));
    setAllGeoZones([...selectedGeoZones, ...rest]);
  }, [selectedGeoZones, geoZones]);

  // Нажатие на кнопку сохранения
  const save = () => {
    // Если выбрана геозона из списка, запрос на put
    if (!nameGeoZone) {
      setErrors((prev) => ({
        ...prev,
        name: true,
      }));
      return;
    }
    if (changeableGeoZone) {
      const body = {
        ...changeableGeoZone,
        name: nameGeoZone,
        area: coordsGeoZone ? coordsToString(coordsGeoZone) : changeableGeoZone.area,
      };
      putGeoZone(body)
        .then((res: { data: any }) => {
          const updatedGeoZone = res.data;
          setSelectedGeoZones((prev) => updateGeoZoneList(prev, updatedGeoZone));
          setGeoZones((prev) => updateGeoZoneList(prev, updatedGeoZone));
          // Сбрасываем координаты после сохранения, чтобы они не применились к следующей геозоне (#13338)
          setCoordsGeoZone(null);
          setIsChanged(false);
        })
        .catch((error: Error) => {
          throw error;
        });
    } else if (isCreateGeoZone) {
      // если геозона не выбрана и состояние создания true, запрос на post
      if (!coordsGeoZone) {
        setErrors((prev) => ({
          ...prev,
          polygon: true,
        }));
        return;
      }
      const body = {
        company: { companyId: company?.companyId },
        name: nameGeoZone,
        area: coordsToString(coordsGeoZone),
      };
      postGeoZone(body)
        .then((newGeoZone: GeoZone) => {
          // Не знаем - на какую страницу попадает новая геозона, поэтому добавляем ее в начало,
          // пока не найдем на загруженной странице
          setGeoZones((prev) => [newGeoZone, ...prev]);
          setSelectedGeoZones((prev) => [...prev, newGeoZone]);
          setNameGeoZone(newGeoZone.name);
          setIsCreateGeoZone(false);
          setIsCreatePolygon(false);
          // Сбрасываем координаты созданной геозоны, чтобы они не применились к другим (#13338)
          setCoordsGeoZone(null);
          setIsChanged(false);
        })
        .catch((error: Error) => {
          throw error;
        });
    }
  };

  // Сброс изменений
  const reset = () => {
    setCoordsGeoZone(null);
    if (!isCreatePolygon) {
      setSelectedGeoZones((prev) => {
        setNameGeoZone(prev?.[0].name || '');
        return JSON.parse(JSON.stringify(prev)) as GeoZone[];
      });
    } else {
      setSelectedGeoZones(allGeoZones[0] ? [allGeoZones[0]] : []);
      setNameGeoZone(allGeoZones[0]?.name || '');
    }
    setIsCreatePolygon(false);
    setIsCreateGeoZone(false);
    setErrors({ name: false, polygon: false });
    setIsChanged(false);
  };

  // Обновление кол-ва изменений при изменении полигона
  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      polygon: false,
    }));
    if (coordsGeoZone) {
      setIsChanged(true);
    }
  }, [coordsGeoZone]);

  const debouncedSearch = useDebounce((value: string) => {
    setAddedZones([]);
    setGeoZones([]);
    setPage(0);
    if (value.length >= 3) {
      getGetGeoZonesFunc(value);
    } else {
      getGetGeoZonesFunc();
    }
  }, debounceDelay, [isExactSearch]);

  useEffect(() => {
    debouncedSearch(search);
  }, [search, debouncedSearch, isExactSearch]);

  useEffect(() => {
    setMaxItems((page + 1) * PAGE_SIZE);
  }, [page]);

  // Загрузка геозон при смене выбранной компании в дереве
  useEffect(() => {
    if (company) {
      setIsCreateGeoZone(false);
      setIsCreatePolygon(false);
      setErrors({ name: false, polygon: false });
      setIsChanged(false);
      setNameGeoZone('');
      getGetGeoZonesFunc('');
    }
  }, [company]);

  useEffect(() => {
    if (maxItems > geoZones.length) {
      if (search.length >= 3) {
        getGetGeoZonesFunc(search);
      } else {
        getGetGeoZonesFunc();
      }
    }
  }, [maxItems]);

  return (
    <Box className={style.companyGeoZonesPanel}>
      <Grid container spacing={2}>
        <Grid item xs={9}>
          <GeoZonesToolbarWidget
            // onFocusName={onFocusName}
            getGetGeoZonesFunc={getGetGeoZonesFunc}
            errors={errors}
            setErrors={setErrors}
            setIsChanged={setIsChanged}
            changeableGeoZone={changeableGeoZone}
            selectedGeoZones={selectedGeoZones}
            nameGeoZone={nameGeoZone}
            onClickCreate={onClickCreate}
            isCreateGeoZone={isCreateGeoZone}
            setNameGeoZone={setNameGeoZone}
            onClickDelete={() => {
              confirm({
                title: 'Удаление',
                confirmationText: 'Да',
                cancellationText: 'Отмена',
                description: `Вы действительно хотите удалить ${changeableGeoZone ? `«${changeableGeoZone.name}»` : `${selectedGeoZones.length} геозон(ы)`}?`,
              })
                .then(() => deleteGeoZones())
                .catch(() => {});
            }}
          />
          {!!selectedGeoZones.length && selectedGeoZones[0]?.polygon === null && (
            <Alert sx={{ width: '100%', marginBottom: '10px' }} severity="error">
              <AlertTitle>Ошибка</AlertTitle>
              Координаты геозоны некорректны либо отсутствуют.
            </Alert>
          )}
          <GeoZonesMapWidget
            changeableGeoZone={changeableGeoZone}
            setCoordsGeoZone={setCoordsGeoZone}
            isCreatePolygon={isCreatePolygon}
            geoZones={selectedGeoZones}
          />
          {errors.polygon ? (
            <Typography variant="body2" gutterBottom sx={{ color: '#D32F2F', paddingLeft: '20px' }}>
              Нарисуйте геозону перед сохранением
            </Typography>
          ) : null}
          <Box className={style.formButtons}>
            <Button disabled={!isChanged && !isCreateGeoZone} disableElevation onClick={reset} variant="text">
              Отмена
            </Button>
            <Button disabled={!isChanged} disableElevation onClick={save} variant="contained">
              Сохранить
            </Button>
          </Box>
        </Grid>
        <GeoZonesSidebar
          search={search}
          setSearch={setSearch}
          setAddedZones={setAddedZones}
          geoZones={geoZones}
          setGeoZones={setGeoZones}
          setSelectedGeoZones={setSelectedGeoZones}
          page={page}
          setPage={setPage}
          getGetGeoZonesFunc={getGetGeoZonesFunc}
          total={total}
          allGeoZones={allGeoZones}
          maxItems={maxItems}
          selectedGeoZones={selectedGeoZones}
          onSelectGeoZone={onSelectGeoZone}
          PAGE_SIZE={PAGE_SIZE}
          isExactSearch={isExactSearch}
          setIsExactSearch={setIsExactSearch}
        />
      </Grid>
    </Box>
  );
}

export default CompanyGeoZonesPanel;
